import React, { useState, useEffect, FC } from "react";
import { Button as BButton, Card, Spinner } from "reactstrap";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../../components/Component";
import {
  TableColumnsType,
  Table,
  Flex,
  Tooltip,
  Spin,
  Tag,
  Upload,
  Collapse,
  Descriptions,
} from "antd";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import {
  CloudDownloadOutlined,
  ExclamationCircleTwoTone,
  EyeFilled,
  FileDoneOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import demoData from "./DemoJson.json";
import dayjs from "dayjs";

interface Contract {
  id: string;
  description: string;
  siteName: string;
  version: string;
  tendersFor: string;
  supplierName: string;
  rank: number;
  "terms&conditions": string;
  status: string;
  contractStatus: string;
  materials: Material[];
  works: Work[];
  materialsOrServices: any[];
}
interface Material {
  key: string;
  material: string;
  qtyAsked: string;
  awardedQty: string;
  awardedQtyPercentage: string;
  unitPrice: string;
  needByDate: string;
  deliveryAddress: string;
  lineTotal: string;
  category?: string;
  subcategory?: string;
  uom?: string;
}
interface Work {
  key: string;
  workLineName: string;
  category: string;
  subcategory?: string;
  description: string;
  quantity: string;
  uom: string;
  rate?: string;
  tax?: string;
  needByDate: string;
  lineTotal: string;
  itemType?: string;
}

const ContractView: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contract, setContract] = useState<Contract | null>(null);
  const [mobileView, setMobileView] = useState<boolean>(false);
  const [sm, setSm] = useState<boolean>(false);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [works, setWorks] = useState<Work[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const { id } = useParams();
  const navigate = useNavigate();
  const { Panel } = Collapse;

  useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
      if (window.innerWidth < 992) {
        setMobileView(true);
      } else {
        setMobileView(false);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setTimeout(() => {
        const foundContract = demoData.find((item) => item.tenderID === id);

        if (foundContract) {
          const transformedContract: Contract = {
            id: foundContract.tenderID,
            description: foundContract.description,
            siteName: foundContract.site.projectName,
            version: "1.0",
            tendersFor: foundContract.category,
            supplierName:
              foundContract.selectedSuppliers.length > 0
                ? foundContract.selectedSuppliers[0].supplierName
                : "No supplier selected",
            rank: 1,
            "terms&conditions":
              foundContract.documentSettings.termsAndConditions.join(", "),
            status: foundContract.status,
            contractStatus: foundContract.contractStatus,
            materials: [],
            works: [],
            materialsOrServices: foundContract.materialsOrServices,
          };
          if (foundContract.materialsOrServices) {
            foundContract.materialsOrServices.forEach(
              (item: any, index: number) => {
                if (item.itemType === "Material") {
                  transformedContract.materials.push({
                    key: index.toString(),
                    material: item.materialName,
                    qtyAsked: item.quantity.toString(),
                    awardedQty: item.quantity.toString(),
                    awardedQtyPercentage: "100%",
                    unitPrice: item.unitPrice || "0.00",
                    needByDate: item.needByDate,
                    deliveryAddress: item.deliveryAddress,
                    lineTotal: item.lineTotal || "0.00",
                    category: item.category,
                    uom: item.uom,
                  });
                } else if (item.itemType === "Service") {
                  transformedContract.works.push({
                    key: index.toString(),
                    workLineName: item.workLineName,
                    category: item.category,
                    description: item.description,
                    quantity: item.quantity.toString(),
                    uom: item.uom,
                    needByDate: item.needByDate,
                    lineTotal: item.lineTotal || "0.00",
                    rate: item.unitPrice || "0.00",
                    tax: item.taxAmount || "0.00",
                    itemType: item.itemType,
                  });
                }
              }
            );
          }

          setContract(transformedContract);
          setMaterials(transformedContract.materials);
          setWorks(transformedContract.works);
        } else {
          navigate("/contracts", { replace: true });
        }

        setIsLoading(false);
      }, 500);
    }
  }, [id, navigate]);

  const materialColumns: TableColumnsType<Material> = [
    {
      title: "Material",
      dataIndex: "material",
      key: "material",
      width: 270,
      fixed: "left" as const,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
    },
    {
      title: "Awarded Qty",
      dataIndex: "awardedQty",
      key: "awardedQty",
      width: 100,
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
    },
    {
      title: "Awarded Qty %",
      dataIndex: "awardedQtyPercentage",
      key: "awardedQtyPercentage",
      width: 120,
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 100,
    },
    {
      title: "Need By Date",
      dataIndex: "needByDate",
      key: "needByDate",
      width: 120,
    },
    {
      title: "Delivery Address",
      dataIndex: "deliveryAddress",
      key: "deliveryAddress",
      width: 200,
    },
    {
      title: "Line Total",
      dataIndex: "lineTotal",
      key: "lineTotal",
      width: 120,
      fixed: "right" as const,
    },
  ];

  const workColumns: TableColumnsType<Work> = [
    {
      title: "WO line",
      dataIndex: "workLineName",
      key: "workLineName",
      width: 200,
      fixed: "left" as const,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 250,
      render: (text: string) => (
        <span>
          {text?.length > 25 ? `${text.substring(0, 30)}...` : text}
          {text?.length > 25 && (
            <Tooltip title={text}>
              <Icon name="info" className="info-circle text-info ms-1" />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 120,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 100,
    },
    {
      title: "Tax",
      dataIndex: "tax",
      key: "tax",
      width: 100,
    },
    {
      title: "Need By Date",
      dataIndex: "needByDate",
      key: "needByDate",
      width: 120,
    },
    {
      title: "Line Total",
      dataIndex: "lineTotal",
      key: "lineTotal",
      width: 150,
      fixed: "right" as const,
    },
  ];

  const handleFinalizeContract = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to finalize this contract?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, finalize it!",
      cancelButtonText: "No, cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        if (contract) {
          setContract({
            ...contract,
            status: "Contract Finalized",
            contractStatus: "Contract Finalized",
          });
        }

        Swal.fire(
          "Finalized!",
          "The contract has been finalized.",
          "success"
        ).then(() => {
          navigate("/contract-list");
        });
      }
    });
  };

  const handleProcessWithPO = () => {
    alert(`Contract ${id} is being processed with PO!`);
  };

  const renderMaterialInfoPanel = () => (
    <Panel header="Material Information" key="1">
      <div className="table-responsive">
        <div
          style={{
            width: "100%",
            minWidth: "1200px",
            overflowX: "auto",
            margin: "auto",
          }}
        >
          <Table
            className="customTable"
            columns={materialColumns}
            dataSource={materials}
            loading={isLoading}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <span
                  style={{ height: "300px", color: "#333", fontSize: "16px" }}
                >
                  No materials found for this contract.
                </span>
              ),
            }}
            bordered
            size="middle"
            pagination={false}
          />
        </div>
      </div>
    </Panel>
  );

  const renderPoDetailsPanel = () => (
    <Panel header="PO Details" key="3">
      <div className="p-0">
        <Descriptions
          title={undefined}
          className="custom-description-title ps-1"
        >
          <Descriptions.Item label="PO #">MH11042589003</Descriptions.Item>
          <Descriptions.Item label="PO Release Date">
            {dayjs("2022-05-20 20:00:10").format("DD-MMM-YYYY HH:mm:ss")}
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Status">
            <Tag
              color={"gold"}
              icon={<ExclamationCircleTwoTone twoToneColor="#faad14" />}
            >
              Pending
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Invoice Status">
            <Tag
              color={"gold"}
              icon={<ExclamationCircleTwoTone twoToneColor="#faad14" />}
            >
              Pending
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Payment Status">
            <Tag
              color={"gold"}
              icon={<ExclamationCircleTwoTone twoToneColor="#faad14" />}
            >
              Pending
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Panel>
  );

  const renderWorkInfoPanel = () => (
    <Panel header="Work Information" key="2">
      <div className="table-responsive">
        <div
          style={{
            width: "100%",
            minWidth: "1200px",
            overflowX: "auto",
            margin: "auto",
          }}
        >
          <Table
            className="customTable"
            columns={workColumns}
            dataSource={works}
            loading={isLoading}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <span
                  style={{ height: "300px", color: "#333", fontSize: "16px" }}
                >
                  No work found for this contract.
                </span>
              ),
            }}
            bordered
            size="middle"
            pagination={false}
          />
        </div>
      </div>
    </Panel>
  );

  return (
    <>
      <Head title="Contract Details" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            {isLoading ? (
              <Flex align="center" gap="middle" className="pageLoader">
                <Spin size="large" className="m-auto" />
              </Flex>
            ) : (
              <div
                className="card-inner card-inner-lg"
                style={{ minHeight: "87vh" }}
              >
                {sm && mobileView && (
                  <div
                    className="toggle-overlay"
                    onClick={() => setSm(!sm)}
                  ></div>
                )}
                <BlockHead size="lg">
                  <BlockBetween>
                    <BlockHeadContent>
                      <BlockTitle tag="h4">
                        Tender ID - {contract?.id}
                        <span style={{ color: "#888" }}> | </span>
                        {contract?.contractStatus === "Contract Finalized" ? (
                          <>
                            <Tag color={"orange"} icon={<TrophyOutlined />}>
                              Awarded
                            </Tag>
                          </>
                        ) : (
                          contract?.contractStatus
                        )}{" "}
                        {contract?.contractStatus === "Contract Finalized" && (
                          <>
                            <span style={{ color: "#888" }}> | </span>
                            <span style={{ color: "#DAA520", fontSize: 18 }}>
                              Score: <strong>86.6/100</strong>
                            </span>
                            <span style={{ color: "#888" }}> | </span>
                            <Tag color="success">Rank #1</Tag>
                          </>
                        )}
                      </BlockTitle>
                    </BlockHeadContent>
                    <div className="d-flex">
                      <BButton
                        className="toggle"
                        color="primary"
                        onClick={() => navigate(`/contract-list`)}
                      >
                        <Icon name="arrow-left" />
                        <span>Back</span>
                      </BButton>
                    </div>
                  </BlockBetween>
                </BlockHead>

                {contract && (
                  <div
                    className="d-flex justify-content-between align-items-center mb-4"
                    style={{
                      background: "#f5f6fa",
                      padding: "12px 18px",
                      borderRadius: 8,
                      flexWrap: "wrap",
                      gap: "8px",
                      fontSize: "14px",
                    }}
                  >
                    <div>
                      <strong>Tender ID:</strong> {contract.id}
                    </div>
                    <div>
                      <strong>Site Name:</strong> {contract.siteName}
                    </div>
                    <div>
                      <strong>Category:</strong> {contract.tendersFor}
                    </div>
                    {/* <div><strong>Status:</strong> {contract.status}</div> */}
                    <div>
                      <strong>Supplier:</strong> {contract.supplierName}
                    </div>
                  </div>
                )}

                <Collapse
                  defaultActiveKey={
                    contract?.tendersFor === "Services"
                      ? ["2", "3"]
                      : ["1", "3"]
                  }
                  className="mb-4"
                >
                  {materials.length > 0 && renderMaterialInfoPanel()}
                  {works.length > 0 && renderWorkInfoPanel()}

                  <Panel
                    header={
                      contract?.contractStatus === "Contract Finalized"
                        ? "Contracts with Suppliers"
                        : "Upload Contracts with Suppliers"
                    }
                    key="3"
                  >
                    {contract?.contractStatus === "Contract Finalized" ? (
                      <div style={{ padding: "12px 0" }}>
                        <strong
                          style={{
                            color: "#333",
                            fontWeight: "bold",
                            display: "block",
                            marginBottom: "8px",
                          }}
                        >
                          Uploaded Contracts:
                        </strong>
                        <ul
                          style={{
                            listStyle: "none",
                            padding: 0,
                            margin: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <li
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "8px 12px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "4px",
                            }}
                          >
                            <FileDoneOutlined color="primary" />
                            <a
                              href={"/doc's/Tender_Summary_NSIC.pdf"}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#1890ff",
                                textDecoration: "underline",
                              }}
                            >
                              Tender_Summary_NSIC.pdf
                            </a>
                          </li>
                          <li
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "8px 12px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "4px",
                            }}
                          >
                            <FileDoneOutlined color="primary" />
                            <a
                              href={
                                "/doc's/1401250132996-b-Works Contract-Regulations.pdf"
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#1890ff",
                                textDecoration: "underline",
                              }}
                            >
                              1401250132996-b-Works Contract-Regulations.pdf
                            </a>
                          </li>
                          <li
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "8px 12px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "4px",
                            }}
                          >
                            <FileDoneOutlined color="primary" />
                            <a
                              href={"/doc's/202291191115.pdf"}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#1890ff",
                                textDecoration: "underline",
                              }}
                            >
                              202291191115.pdf
                            </a>
                          </li>
                        </ul>
                      </div>
                    ) : (
                      <div style={{ padding: "12px 0" }}>
                        <strong
                          style={{
                            color: "#333",
                            fontWeight: "bold",
                            display: "block",
                            marginBottom: "8px",
                          }}
                        >
                          Upload Contracts:
                        </strong>
                        <Upload
                          name="file"
                          multiple={true}
                          onChange={(info) => {
                            if (info.file.status === "done") {
                              setUploadedFiles((prev) => [...prev, info.file]);
                            }
                          }}
                          beforeUpload={(file) => {
                            setUploadedFiles((prev) => [...prev, file]);
                            return false; // Prevent automatic upload
                          }}
                          onRemove={(file) => {
                            setUploadedFiles((prev) =>
                              prev.filter((f) => f.uid !== file.uid)
                            );
                          }}
                          fileList={uploadedFiles}
                          style={{ width: "100%" }}
                        >
                          <BButton color="btn-dim btn">
                            <span>Upload </span>
                            <Icon name="upload-cloud" />
                          </BButton>
                        </Upload>
                      </div>
                    )}
                  </Panel>
                  {contract?.contractStatus === "Contract Finalized" &&
                    renderPoDetailsPanel()}
                </Collapse>

                {isLoading ? (
                  <BButton color="primary" size="lg" type="button">
                    <Spinner size="sm" color="light" />
                  </BButton>
                ) : (
                  <div
                    className="actions clearfix"
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "16px",
                    }}
                  >
                    {contract?.contractStatus !== "Contract Finalized" && (
                      <>
                        <BButton
                          color="primary"
                          className="btn-dim btn btn-secondary"
                        >
                          SAVE AS DRAFT
                        </BButton>
                        <BButton
                          color="primary"
                          onClick={handleFinalizeContract}
                          disabled={uploadedFiles.length === 0}
                        >
                          FINALIZE CONTRACT
                        </BButton>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </Content>
    </>
  );
};

export default ContractView;
